import { skipToken, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { RevisionRaster, getHCCApi } from "@nens/threedi/openapi-client";

import { queryDirectly } from "./hooks/query-directly";

// There are multiple ways to download a raster. Either by its model id and raster id, or by its schematisation id, revision id and raster id.
// They return the same download url so it can be gotten in 1 query.

type RasterDownloadArgs =
  | {
      schematisationId: number;
      revisionId: number | undefined;
      raster: RevisionRaster | undefined;
    }
  | {
      modelId: number | undefined;
      raster: RevisionRaster | undefined;
    };

const getHccRasterDownloadQuery = (args: RasterDownloadArgs) => {
  const queryKey = ["hcc-raster-download", args];

  let queryFn: (() => Promise<string | undefined>) | typeof skipToken = skipToken;
  const { raster } = args;
  const rasterId = raster?.id;
  if (rasterId) {
    if ("modelId" in args) {
      const { modelId } = args;

      if (modelId) {
        queryFn = () =>
          getHCCApi()
            .threedimodelsRastersDownload({
              id: rasterId,
              threedimodelPk: modelId,
            })
            .then((result) => result.getUrl);
      }
    } else {
      const { schematisationId, revisionId } = args;
      if (revisionId) {
        queryFn = () =>
          getHCCApi()
            .schematisationsRevisionsRastersDownload({
              id: rasterId,
              revisionPk: revisionId,
              schematisationPk: schematisationId,
            })
            .then((result) => result.getUrl);
      }
    }
  }

  return getRanaApiV1().getQueryOptions<string | undefined>({
    enabled: raster?.file?.state === "uploaded",
    queryKey,
    queryFn,
  });
};

export const useHccRasterDownloadQuery = (args: RasterDownloadArgs) => {
  return useQuery(getHccRasterDownloadQuery(args));
};

export const useHccRasterDownloadQueries = (argsList: RasterDownloadArgs[]) => {
  return useQueries({
    queries: argsList.map((args) => getHccRasterDownloadQuery(args)),
  });
};

export const useHccRasterDownloadQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (args: Required<RasterDownloadArgs>) =>
    queryDirectly(queryClient, getHccRasterDownloadQuery(args));
};
